import { useState } from 'react';
import { Mail, Star, Trash2 } from 'lucide-react';
import {
  useAddIdentityEmail,
  useDeleteIdentityEmail,
  useIdentityEmails,
  useResendIdentityEmail,
  useSetPrimaryIdentityEmail,
} from '@/hooks/useIdentityEmails';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

/** Identity (sign-in / recovery) email addresses — GitHub-style multi-email.
 * Adding one requires confirming a verification email before it can be used. */
export function IdentityEmails() {
  const { data: emails } = useIdentityEmails();
  const add = useAddIdentityEmail();
  const setPrimary = useSetPrimaryIdentityEmail();
  const resend = useResendIdentityEmail();
  const del = useDeleteIdentityEmail();
  const [email, setEmail] = useState('');

  const submit = () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    add.mutate(trimmed, { onSuccess: () => { setEmail(''); } });
  };

  return (
    <section className="bg-card border border-border rounded-lg">
      <header className="px-5 py-4 border-b border-border">
        <h2 className="text-sm font-semibold">Email addresses</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Addresses you can sign in and recover your account with. New addresses must be confirmed
          before use. The primary address is where account email goes.
        </p>
      </header>
      <div className="p-5 space-y-2">
        {(emails ?? []).map((e) => (
          <div key={e.id} className="flex items-center justify-between gap-3 rounded-md border border-border-subtle px-3 py-2">
            <span className="flex items-center gap-2 text-sm min-w-0">
              <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="truncate">{e.email}</span>
              {e.primary ? <Badge className="bg-sev-5/15 text-sev-5 border-0 font-normal">Primary</Badge> : null}
              {e.verified ? (
                <Badge className="bg-sev-4/15 text-sev-4 border-0 font-normal">Verified</Badge>
              ) : (
                <Badge className="bg-sev-3/15 text-foreground border-0 font-normal">Pending</Badge>
              )}
            </span>
            <span className="flex items-center gap-1.5 shrink-0">
              {e.verified && !e.primary ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setPrimary.mutate(e.id); }}
                  disabled={setPrimary.isPending}
                >
                  <Star className="h-3 w-3 mr-1" />
                  Make primary
                </Button>
              ) : null}
              {!e.verified ? (
                <Button size="sm" variant="outline" onClick={() => { resend.mutate(e.id); }} disabled={resend.isPending}>
                  Resend
                </Button>
              ) : null}
              {!e.primary ? (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => { del.mutate(e.id); }}
                  disabled={del.isPending}
                  aria-label={`Remove ${e.email}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              ) : null}
            </span>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <Input
            type="email"
            value={email}
            onChange={(ev) => { setEmail(ev.target.value); }}
            placeholder="another@example.com"
            className="h-8 text-sm"
          />
          <Button size="sm" onClick={submit} disabled={add.isPending || !email.trim()}>
            {add.isPending ? 'Adding…' : 'Add email'}
          </Button>
        </div>
        {add.isError ? <p className="text-xs text-destructive">That email is already in use or invalid.</p> : null}
      </div>
    </section>
  );
}
